/**
 * Error handling utilities specifically designed to catch and handle
 * animation and DOM-related errors gracefully
 */
import { evictThreadCaches, markFirestoreMemoryMode } from './storageHeadroom';
import { carryAcrossReload, noteEvent } from './chatHealthCounters';

/**
 * Check if an error is animation-related
 * @param error The error object or message to check
 * @returns boolean indicating if the error is animation-related
 */
export const isAnimationError = (error: Error | string | unknown): boolean => {
  const errorMessage = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  return (
    errorMessage.includes("Cannot use 'in' operator to search for 'animation'") ||
    (errorMessage.includes("animation") && errorMessage.includes("undefined")) ||
    (errorMessage.includes("Cannot read property") && errorMessage.includes("animation")) ||
    (errorMessage.includes("Cannot set properties of undefined") && errorMessage.includes("animation"))
  );
};

/**
 * Check if an error is DOM position-related
 * @param error The error object or message to check
 * @returns boolean indicating if the error is position-related
 */
export const isPositionError = (error: Error | string | unknown): boolean => {
  const errorMessage = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  return (
    (errorMessage.includes("Cannot set properties of undefined") && errorMessage.includes("position")) ||
    (errorMessage.includes("Cannot read property") && errorMessage.includes("position")) ||
    (errorMessage.includes("Cannot read property") && 
      (errorMessage.includes("top") || 
       errorMessage.includes("left") || 
       errorMessage.includes("right") || 
       errorMessage.includes("bottom")))
  );
};

/**
 * Firestore localStorage-quota crash class (INCIDENT 2026-08-22).
 *
 * With multi-tab persistence on, the Firestore SDK coordinates tabs through
 * localStorage keys (`firestore_mutations_*`, `firestore_targets_*`, …). A
 * message flood filled the 5MB budget; `setItem` started throwing
 * QuotaExceededError inside the SDK, which then died with
 * "FIRESTORE (x.y.z) INTERNAL ASSERTION FAILED: Unexpected state (ID: b815)"
 * — a red error wall the user cannot recover from without knowing to clear
 * site data by hand. Users must never need to know that (2026-08-20 cache
 * doctrine, same spirit). Precision-first: a quota error is only claimed when
 * the message also names firestore — some other feature filling storage is
 * not this class. Every internal assertion is claimed: whatever broke the
 * SDK's queue, the tab only recovers by reloading.
 */
export const isFirestoreQuotaCrash = (error: Error | string | unknown): boolean => {
  let name = '';
  let text = '';
  if (typeof error === 'string') {
    text = error;
  } else if (error && typeof error === 'object') {
    // Not `instanceof Error`: DOMException (QuotaExceededError's real type)
    // fails that check in some browsers, and event.reason can be anything.
    name = String((error as { name?: unknown }).name ?? '');
    const message = String((error as { message?: unknown }).message ?? '');
    text = name ? `${name}: ${message || String(error)}` : message || String(error);
  } else {
    text = String(error ?? '');
  }
  if (/FIRESTORE.+INTERNAL ASSERTION FAILED/i.test(text)) return true;
  const isQuota = name === 'QuotaExceededError' || /QuotaExceededError|exceeded the quota/i.test(text);
  return isQuota && /firestore/i.test(text);
};

const QUOTA_SELF_HEAL_STAMP_KEY = 'prest0n_firestore_quota_selfheal_at';
const QUOTA_SELF_HEAL_MIN_INTERVAL_MS = 5 * 60 * 1000;
/** The heal frees at least this much localStorage (UTF-16 code units, ~2 MB). */
export const QUOTA_SELF_HEAL_RECLAIM_CHARS = 1_048_576;
/**
 * The flush event every reload-before hook listens for. The same literal as
 * staleTabSelfHeal.SELF_HEAL_BEFORE_RELOAD_EVENT (pinned equal by test):
 * importing that module here would pull the update banner into the entry
 * chunk's error handlers.
 */
export const HEAL_BEFORE_RELOAD_EVENT = 'prest0:before-self-heal-reload';

/**
 * Recover a tab whose Firestore SDK has died (2026-08-22; reworked 2026-09-28,
 * Malaika's freezes).
 *
 * Every time it is asked, it first leaves the storage able to start Firestore
 * again: this tab is marked to start on Firestore's in-memory cache for the
 * next half hour (the persistent cache is what broke), and cached thread
 * transcripts are evicted to make room (the server holds every message). Until
 * 2026-09-28 it deleted only the SDK's own few-hundred-byte keys, so a nearly
 * full bin failed the SDK again straight after the reload, and a hard refresh
 * inside the loop guard's window left the person stuck until they closed the
 * browser.
 *
 * Then it reloads — at most once per QUOTA_SELF_HEAL_MIN_INTERVAL_MS, guarded
 * by a sessionStorage stamp (a separate quota, so it has room even when
 * localStorage is full), so a heal that does not stick surfaces the error
 * instead of reload-looping. `force` (a person's click on the refresh card)
 * skips that guard: a click cannot loop. Returns true iff a reload was
 * initiated.
 *
 * Dropping `firestore_*` keys is safe-by-comparison, not free: any client
 * mutations still queued in the crashed tab are abandoned. That tab was
 * already dead, and a clean reload resumes from the server, which is strictly
 * better than a permanently frozen page.
 */
let healPreparedThisPage = false;
// The health record hears about a declined heal once per page (2026-09-28).
let healDeclineNoted = false;
const noteHealDeclined = (): void => {
  if (healDeclineNoted) return;
  healDeclineNoted = true;
  noteEvent('firestore-heal-declined');
};

export const attemptFirestoreQuotaSelfHeal = (opts: { force?: boolean } = {}): boolean => {
  // Once per page: a dead SDK logs an error for every call made to it, and
  // each one asks for a heal.
  if (!healPreparedThisPage) {
    healPreparedThisPage = true;
    // The chat's health record (2026-09-28): the queue failed on this page.
    noteEvent('firestore-queue-failed');
    try {
      markFirestoreMemoryMode();
    } catch {
      /* no marker: the next load probes for room instead */
    }
    try {
      const ls = window.localStorage;
      if (ls) evictThreadCaches(ls, QUOTA_SELF_HEAL_RECLAIM_CHARS);
    } catch {
      /* nothing could be freed; the next load decides for itself */
    }
  }
  try {
    try {
      const last = Number(window.sessionStorage.getItem(QUOTA_SELF_HEAL_STAMP_KEY) || 0);
      if (
        !opts.force
        && Number.isFinite(last)
        && last > 0
        && Date.now() - last < QUOTA_SELF_HEAL_MIN_INTERVAL_MS
      ) {
        noteHealDeclined();
        return false; // healed recently and still crashing — do not loop
      }
      window.sessionStorage.setItem(QUOTA_SELF_HEAL_STAMP_KEY, String(Date.now()));
    } catch {
      if (!opts.force) { noteHealDeclined(); return false; } // cannot guard against a loop → do not reload at all
    }
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firestore_')) doomed.push(key);
    }
    for (const key of doomed) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // a single stuck key must not stop the rest
      }
    }
    try {
      window.dispatchEvent(new CustomEvent(HEAL_BEFORE_RELOAD_EVENT));
    } catch {
      /* a listener problem must not stop the heal */
    }
    // The health record keeps what it heard across the reload (2026-09-28).
    noteEvent('firestore-self-heal');
    carryAcrossReload();
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

/** For tests: a new page. */
export const __resetFirestoreHealForTests = (): void => {
  healPreparedThisPage = false;
  healDeclineNoted = false;
};

/**
 * Setup global error handlers for animation and DOM-related errors
 */
export const setupGlobalErrorHandlers = (): void => {
  // Store original console methods
  const originalConsoleError = console.error;
  
  // Patch console.error to intercept animation errors
  console.error = function(...args: any[]) {
    // Firestore quota crash: the SDK logs the internal assertion through its
    // own logger (console.error) besides throwing, and the throw sometimes
    // dies inside SDK internals no listener sees — so this patch is a catch
    // point too. Log FIRST (the evidence must survive for support), then heal.
    if (args.some((arg) => isFirestoreQuotaCrash(arg))) {
      originalConsoleError.apply(console, args);
      attemptFirestoreQuotaSelfHeal();
      return;
    }

    // Check if this is an animation-related error we want to handle gracefully
    const isAnimation = args.some(arg =>
      typeof arg === 'string' && isAnimationError(arg)
    );

    const isPosition = args.some(arg =>
      typeof arg === 'string' && isPositionError(arg)
    );

    // If it's a handled error type, downgrade to warning
    if (isAnimation) {
      console.warn('Animation error intercepted:', ...args);
      return;
    }
    
    if (isPosition) {
      console.warn('Position error intercepted:', ...args);
      return;
    }
    
    // Otherwise, use the original error handler
    originalConsoleError.apply(console, args);
  };
  
  // Handle uncaught promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isFirestoreQuotaCrash(event.reason)) {
      // Only swallow the rejection when a reload is actually underway;
      // if the heal declined (recent reload, guard unusable) the error
      // must stay visible.
      if (attemptFirestoreQuotaSelfHeal()) event.preventDefault();
      return;
    }
    if (isAnimationError(event.reason) || isPositionError(event.reason)) {
      // Prevent the error from bubbling up and crashing the app
      event.preventDefault();
      console.warn('Suppressed error in promise:', event.reason);
    }
  });

  // Handle uncaught exceptions
  window.addEventListener('error', (event) => {
    if (isFirestoreQuotaCrash(event.error) || isFirestoreQuotaCrash(event.message)) {
      if (attemptFirestoreQuotaSelfHeal()) {
        event.preventDefault();
        return true;
      }
      return false;
    }
    if (isAnimationError(event.error) || isPositionError(event.error)) {
      // Prevent the error from bubbling up and crashing the app
      event.preventDefault();
      console.warn('Suppressed error:', event.error);
      return true; // Prevents the firing of the default event handler
    }
    return false; // Let other errors propagate normally
  });
};

/**
 * Create a safe version of a function that might throw animation or position errors
 * @param fn The function to make safe
 * @returns A wrapped version of the function that won't throw
 */
export function makeSafe<T extends (...args: any[]) => any>(
  fn: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return (...args: Parameters<T>): ReturnType<T> | undefined => {
    try {
      return fn(...args);
    } catch (error) {
      if (isAnimationError(error) || isPositionError(error)) {
        console.warn('Caught and suppressed error in function:', error);
        return undefined;
      }
      throw error; // Re-throw any other errors
    }
  };
} 